import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Paper,
    Box,
    Typography,
    TextField,
    RadioGroup,
    FormControlLabel,
    Radio,
    FormGroup,
    Checkbox,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Alert,
    Divider,
} from '@mui/material';
import { db, auth } from '../firebase/config';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {
    doc,
    getDoc,
    addDoc,
    collection,
    query,
    where,
    getDocs
} from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import logo from "./Nims_Logo.png"

export default function FormSubmission() {
    const { formId } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const [existingSubmission, setExistingSubmission] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [responses, setResponses] = useState({});
    const [alreadySubmitted, setAlreadySubmitted] = useState(false);
    const [successOpen, setSuccessOpen] = useState(false);



    useEffect(() => {
        if (!user) {
            setError('User not authenticated. Please log in.');
            setLoading(false);
            return;
        }

        fetchForm();
        checkIfAlreadySubmitted();
    }, [formId, user]);


    const fetchForm = async () => {
        try {
            setLoading(true);
            const formDoc = await getDoc(doc(db, 'forms', formId));
            if (formDoc.exists()) {
                setForm(formDoc.data());
            } else {
                setError('Form not found');
            }
        } catch (err) {
            console.error('Error fetching form:', err);
            setError('Failed to load form');
        } finally {
            setLoading(false);
        }
    };
    const checkIfAlreadySubmitted = async () => {
        try {
            const q = query(
                collection(db, 'submissions'),
                where('formId', '==', formId),
                where('userId', '==', user.uid)
            );

            const snapshot = await getDocs(q);

            if (!snapshot.empty) {
                const submissionDoc = snapshot.docs[0].data();

                setAlreadySubmitted(true);
                setExistingSubmission(submissionDoc);

                // ✅ LOAD PREVIOUS RESPONSES
                setResponses(submissionDoc.responses || {});
            }
        } catch (err) {
            console.error('Error checking submission:', err);
        }
    };



    const handleResponseChange = (questionId, value) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: value
        }));
    };

    const handleSubmit = async () => {
        try {
            if (!user) {
                setError('User not authenticated');
                return;
            }

            setSubmitting(true);

            const firstName = userProfile.firstName || 'Student';
            const lastName = userProfile.lastName || '';
            const studentName = `${firstName} ${lastName}`.trim();

            const cleanResponses = {};
            Object.keys(responses).forEach(key => {
                let value = responses[key];
                if (Array.isArray(value)) {
                    cleanResponses[key] = value.join(', ');
                } else {
                    cleanResponses[key] = value || '';
                }
            });

            // console.log('📝 Submitting form:', { formId, studentName, form: form?.formType });
            // console.log('📝 Submitting form:', { firstName });
            // console.log('📝 Clean responses:', cleanResponses);

            const questionScores = {};
            let totalObtainedScore = 0;
            let totalMaxScore = 0;

            if (form.formType === 'evaluation' && form.questions && form.questions.length > 0) {
                // console.log('✅ Form is evaluation type, calculating scores...');
                // console.log('📝 Number of questions:', form.questions.length);

                form.questions.forEach((question, idx) => {
                    const qKey = `q${idx}`;
                    const maxScore = question.maxScore || 10;
                    const userAnswer = responses[qKey];

                    totalMaxScore += maxScore;

                    let obtainedScore = 0;

                    if (question.type === 'single-select') {
                        if (userAnswer) {
                            const selectedOption = question.options?.find(opt => opt.text === userAnswer);
                            if (selectedOption) {
                                obtainedScore = selectedOption.score || 0;
                            }
                        }
                    } else if (question.type === 'mcq') {
                        if (Array.isArray(userAnswer) && userAnswer.length > 0) {
                            userAnswer.forEach(answer => {
                                const selectedOption = question.options?.find(opt => opt.text === answer);
                                if (selectedOption) {
                                    obtainedScore += selectedOption.score || 0;
                                }
                            });
                        }
                    } else if (question.type === 'paragraph') {
                        if (userAnswer && userAnswer.trim().length > 0) {
                            obtainedScore = maxScore;
                        }
                    }

                    questionScores[qKey] = {
                        obtained: obtainedScore,
                        maxScore: maxScore,
                        percentage: maxScore > 0 ? Math.round((obtainedScore / maxScore) * 100) : 0
                    };

                    totalObtainedScore += obtainedScore;

                    // console.log(`Question ${idx} (${qKey}):`, {
                    //     answer: userAnswer,
                    //     obtained: obtainedScore,
                    //     maxScore: maxScore,
                    //     percentage: questionScores[qKey].percentage
                    // });
                });

                // console.log('📊 Final scores:', {
                //     questionScores,
                //     totalObtainedScore,
                //     totalMaxScore,
                //     percentage: Math.round((totalObtainedScore / totalMaxScore) * 100)
                // });
            }

            const totalPercentage = totalMaxScore > 0
                ? Math.round((totalObtainedScore / totalMaxScore) * 100)
                : 0;

            let finalStatus = 'Pending';
            if (form.formType === 'evaluation') {
                if (totalPercentage >= 80) {
                    finalStatus = 'Admit';
                } else if (totalPercentage >= 50) {
                    finalStatus = 'Conditional';
                } else if (totalPercentage > 0) {
                    finalStatus = 'Reject';
                }
            } else {
                finalStatus = 'Submitted';
            }

            // console.log('📊 Final Status:', finalStatus);
            // console.log('📊 Total Score:', `${totalObtainedScore}/${totalMaxScore} (${totalPercentage}%)`);

            const docData = {
                formId: formId,
                firstName: firstName,
                lastName: lastName,
                responses: cleanResponses,
                studentName: studentName,
                questionScores: questionScores,
                totalObtainedScore: totalObtainedScore,
                totalMaxScore: totalMaxScore,
                totalPercentage: totalPercentage,
                finalStatus: finalStatus,
                status: 'submitted',
                submittedAt: new Date(),
                userId: user.uid,
                userEmail: user.email,
                formType: form.formType
            };

            // console.log('💾 Saving document:', docData);

            const docRef = await addDoc(collection(db, 'submissions'), docData);

            // console.log('✅ SUCCESS! Submission saved:', docRef.id);
            // console.log('📊 With scores:', {
            //     totalObtainedScore,
            //     totalMaxScore,
            //     totalPercentage,
            //     finalStatus
            // });

            setError('');
            // alert(`Form submitted! Score: ${totalObtainedScore}/${totalMaxScore} (${totalPercentage}%)`);

            setResponses({});
            setSuccessOpen(true);

            // redirect to home after 2 seconds
            setTimeout(() => {
                setSuccessOpen(false);
                navigate('/'); // 👈 home route
            }, 2000);


        } catch (error) {
            console.error('❌ Error saving submission:', error);
            setError(`Error: ${error.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #dc2626 100%)',
                        py: { xs: 1.5, md: 2 },
                        px: 2,
                        boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)',
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                            {/* Logo */}
                            <Box
                                component="img"
                                src={logo}
                                alt="Logo"
                                sx={{
                                    width: { xs: 160, sm: 190, md: 230 },   // 👈 responsive width
                                    height: { xs: 48, sm: 60, md: 70 },     // 👈 responsive height
                                    borderRadius: 1,
                                    objectFit: 'contain',
                                    backgroundColor: 'white',
                                    p: { xs: '4px', md: '6px' },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    flexShrink: 0
                                }}
                            />

                            {/* Title */}
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                {form?.title || 'Form Submission Portal'}
                            </Typography>
                        </Box>
                    </Container>
                </Box>



            </Box>
        );
    }

    if (error && !form) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #dc2626 100%)',
                        py: { xs: 1.5, md: 2 },
                        px: 2,
                        boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)',
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                            <img
                                src={logo}
                                alt="Logo"
                                style={{
                                    width: 45, //na
                                    height: 45,
                                    borderRadius: 8,
                                    objectFit: 'contain',
                                    backgroundColor: 'white',
                                    padding: '6px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    flexShrink: 0
                                }}
                            />
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Form Submission Portal
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                <Container maxWidth="md" sx={{ py: 6 }}>
                    <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
                </Container>
            </Box>
        );
    }

    if (!form) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #dc2626 0%, #653a8a 100%)',
                        py: { xs: 1.5, md: 2 },
                        px: 2,
                        boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)',
                    }}
                >
                    <Container maxWidth="lg">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                            {/* Logo */}
                            <Box
                                component="img"
                                src={logo}
                                alt="Logo"
                                sx={{
                                    width: { xs: 160, sm: 190, md: 230 },   // 👈 responsive width
                                    height: { xs: 48, sm: 60, md: 70 },     // 👈 responsive height
                                    borderRadius: 1,
                                    objectFit: 'contain',
                                    backgroundColor: 'white',
                                    p: { xs: '4px', md: '6px' },
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                    flexShrink: 0
                                }}
                            />

                            {/* Title */}
                            <Typography
                                variant="h6"
                                sx={{
                                    color: 'white',
                                    fontWeight: 700,
                                    letterSpacing: 0.3,
                                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                {form?.title || 'Form Submission Portal'}
                            </Typography>
                        </Box>
                    </Container>
                </Box>

                <Container maxWidth="md" sx={{ py: 6 }}>
                    <Alert severity="error" sx={{ borderRadius: 2 }}>Form not found</Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #dc2626 0%, #653a8a 100%)',
                    py: { xs: 1.5, md: 2 },
                    px: 2,
                    boxShadow: '0 4px 20px rgba(220, 38, 38, 0.15)',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1100,
                }}
            >
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 } }}>
                        {/* Logo */}
                        <Box
                            component="img"
                            src={logo}
                            alt="Logo"
                            sx={{
                                width: { xs: 160, sm: 190, md: 230 },   // 👈 responsive width
                                height: { xs: 48, sm: 60, md: 70 },     // 👈 responsive height
                                borderRadius: 1,
                                objectFit: 'contain',
                                backgroundColor: 'white',
                                p: { xs: '4px', md: '6px' },
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                flexShrink: 0
                            }}
                        />

                        {/* Title */}
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'white',
                                fontWeight: 700,
                                letterSpacing: 0.3,
                                fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                                lineHeight: 1.2
                            }}
                        >
                            {form?.title || 'Form Submission Portal'}
                        </Typography>
                    </Box>

                </Container>
            </Box>

            <Container maxWidth="md" sx={{ py: { xs: 3, md: 6 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        borderRadius: 3,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        border: '1px solid #e2e8f0',
                    }}
                >
                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <CheckCircleOutlineIcon sx={{ color: '#dc2626', fontSize: 28 }} />
                            <Typography
                                variant="h4"
                                sx={{
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontSize: { xs: '1.5rem', md: '2rem' },
                                }}
                            >
                                {form.title}
                            </Typography>
                        </Box>
                        <Typography
                            variant="body1"
                            sx={{ color: '#64748b', lineHeight: 1.7, mb: 2 }}
                        >
                            {form.description}
                        </Typography>
                        <Divider sx={{ borderColor: '#e2e8f0' }} />
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{ mb: 3, borderRadius: 2 }}
                            onClose={() => setError('')}
                        >
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {form.formType === 'evaluation' && (
                            <>
                                {form.questions?.map((question, idx) => (
                                    <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            bgcolor: '#f8fafc',
                                            borderRadius: 2,
                                            border: '1px solid #e2e8f0',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                borderColor: '#667eea',
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 2,
                                                color: '#1e293b',
                                                fontSize: '1.05rem',
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-block',
                                                    bgcolor: '#dc2626',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 28,
                                                    height: 28,
                                                    textAlign: 'center',
                                                    lineHeight: '28px',
                                                    mr: 1.5,
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                {idx + 1}
                                            </Box>
                                            {question.text}
                                            {question.mandatory && (
                                                <Box
                                                    component="span"
                                                    sx={{ color: '#ef4444', ml: 0.5, fontWeight: 700 }}
                                                >
                                                    *
                                                </Box>
                                            )}
                                        </Typography>

                                        {question.type === 'single-select' && (
                                            <RadioGroup
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) =>
                                                    handleResponseChange(`q${idx}`, e.target.value)
                                                }
                                                disabled={alreadySubmitted}
                                            >

                                                {question.options?.map((opt, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        value={opt.text}
                                                        control={
                                                            <Radio
                                                                sx={{
                                                                    color: '#94a3b8',
                                                                    '&.Mui-checked': { color: '##dc2626' },
                                                                }}
                                                            />
                                                        }
                                                        label={opt.text}
                                                        sx={{
                                                            mb: 1,
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '0.95rem',
                                                                color: '#475569',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </RadioGroup>
                                        )}

                                        {question.type === 'mcq' && (
                                            <FormGroup sx={{ ml: 1 }}>
                                                {question.options?.map((opt, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        control={
                                                            <Checkbox
                                                                disabled={alreadySubmitted}

                                                                checked={
                                                                    Array.isArray(responses[`q${idx}`])
                                                                        ? responses[`q${idx}`].includes(opt.text)
                                                                        : false
                                                                }
                                                                onChange={(e) => {
                                                                    const current = responses[`q${idx}`] || [];
                                                                    const updated = e.target.checked
                                                                        ? [...current, opt.text]
                                                                        : current.filter((item) => item !== opt.text);
                                                                    handleResponseChange(`q${idx}`, updated);
                                                                }}
                                                                sx={{
                                                                    color: '#94a3b8',
                                                                    '&.Mui-checked': { color: '#dc2626' },
                                                                }}
                                                            />
                                                        }
                                                        label={opt.text}
                                                        sx={{
                                                            mb: 1,
                                                            '& .MuiFormControlLabel-label': {
                                                                fontSize: '0.95rem',
                                                                color: '#475569',
                                                            },
                                                        }}
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}

                                        {question.type === 'paragraph' && (
                                            <TextField
                                                disabled={alreadySubmitted}
                                                fullWidth
                                                multiline
                                                rows={4}
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) =>
                                                    handleResponseChange(`q${idx}`, e.target.value)
                                                }
                                                placeholder="Type your answer here..."
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'white',
                                                        '&:hover fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    </Paper>
                                ))}
                            </>
                        )}

                        {form.formType === 'input-detail' && (
                            <>
                                {form.fields?.map((field, idx) => (
                                    <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            bgcolor: '#f8fafc',
                                            borderRadius: 2,
                                            border: '1px solid #e2e8f0',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                borderColor: '#667eea',
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 2,
                                                color: '#1e293b',
                                                fontSize: '1.05rem',
                                            }}
                                        >
                                            {field.label}
                                            {field.required && (
                                                <Box
                                                    component="span"
                                                    sx={{ color: '#ef4444', ml: 0.5, fontWeight: 700 }}
                                                >
                                                    *
                                                </Box>
                                            )}
                                        </Typography>

                                        {['text', 'email', 'number', 'phone'].includes(field.fieldType) && (
                                            <TextField
                                                fullWidth
                                                type={field.fieldType === 'phone' ? 'tel' : field.fieldType}
                                                placeholder={field.placeholder}
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) =>
                                                    handleResponseChange(`f${idx}`, e.target.value)
                                                }
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'white',
                                                        '&:hover fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#653a8a',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}

                                        {field.fieldType === 'textarea' && (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                placeholder={field.placeholder}
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) =>
                                                    handleResponseChange(`f${idx}`, e.target.value)
                                                }
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'white',
                                                        '&:hover fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}

                                        {field.fieldType === 'date' && (
                                            <TextField
                                                fullWidth
                                                type="date"
                                                InputLabelProps={{ shrink: true }}
                                                value={responses[`f${idx}`] || ''}
                                                onChange={(e) =>
                                                    handleResponseChange(`f${idx}`, e.target.value)
                                                }
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'white',
                                                        '&:hover fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        {field.fieldType === 'dropdown' && (
                                            <FormControl fullWidth variant="outlined">
                                                <InputLabel id={`select-label-${idx}`}>
                                                    {field.label}
                                                </InputLabel>

                                                <Select
                                                    labelId={`select-label-${idx}`}
                                                    value={responses[`f${idx}`] || ''}
                                                    onChange={(e) =>
                                                        handleResponseChange(`f${idx}`, e.target.value)
                                                    }
                                                    label={field.label}
                                                    disabled={alreadySubmitted}
                                                    displayEmpty
                                                >
                                                    {/* Placeholder option */}
                                                    <MenuItem value="" disabled>
                                                        Select {field.label}
                                                    </MenuItem>

                                                    {field.options?.map((opt, optIdx) => (
                                                        <MenuItem key={optIdx} value={opt.label}>
                                                            {opt.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {field.fieldType === 'checkbox' && (
                                            <FormGroup>
                                                {field.options?.map((opt, optIdx) => (
                                                    <FormControlLabel
                                                        key={optIdx}
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    Array.isArray(responses[`f${idx}`])
                                                                        ? responses[`f${idx}`].includes(opt.label)
                                                                        : false
                                                                }
                                                                onChange={(e) => {
                                                                    const current = responses[`f${idx}`] || [];
                                                                    const updated = e.target.checked
                                                                        ? [...current, opt.label]
                                                                        : current.filter(item => item !== opt.label);

                                                                    handleResponseChange(`f${idx}`, updated);
                                                                }}
                                                                disabled={alreadySubmitted}
                                                            />
                                                        }
                                                        label={opt.label}
                                                    />
                                                ))}
                                            </FormGroup>
                                        )}

                                    </Paper>
                                ))}
                            </>
                        )}

                        {form.formType === 'feedback' && (
                            <>
                                {form.questions?.map((question, idx) => (
                                    <Paper
                                        key={idx}
                                        elevation={0}
                                        sx={{
                                            p: 3,
                                            bgcolor: '#f8fafc',
                                            borderRadius: 2,
                                            border: '1px solid #e2e8f0',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                                                borderColor: '#667eea',
                                            },
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: 600,
                                                mb: 2,
                                                color: '#1e293b',
                                                fontSize: '1.05rem',
                                            }}
                                        >
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-block',
                                                    bgcolor: '#667eea',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: 28,
                                                    height: 28,
                                                    textAlign: 'center',
                                                    lineHeight: '28px',
                                                    mr: 1.5,
                                                    fontSize: '0.9rem',
                                                }}
                                            >
                                                {idx + 1}
                                            </Box>
                                            {question.text}
                                            {question.required && (
                                                <Box
                                                    component="span"
                                                    sx={{ color: '#653a8a', ml: 0.5, fontWeight: 700 }}
                                                >
                                                    *
                                                </Box>
                                            )}
                                        </Typography>

                                        {question.type === 'rating' && (
                                            <Box sx={{ ml: 1 }}>
                                                <RadioGroup
                                                    row
                                                    value={responses[`q${idx}`] || ''}
                                                    onChange={(e) =>
                                                        handleResponseChange(`q${idx}`, e.target.value)
                                                    }
                                                    sx={{ gap: 1 }}
                                                >
                                                    {Array.from({ length: question.scale || 5 }).map((_, i) => (
                                                        <FormControlLabel
                                                            key={i}
                                                            value={String(i + 1)}
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        color: '#94a3b8',
                                                                        '&.Mui-checked': { color: '#dc2626' },
                                                                    }}
                                                                />
                                                            }
                                                            label={i + 1}
                                                            sx={{
                                                                '& .MuiFormControlLabel-label': {
                                                                    fontSize: '0.95rem',
                                                                    color: '#475569',
                                                                },
                                                            }}
                                                        />
                                                    ))}
                                                </RadioGroup>
                                            </Box>
                                        )}

                                        {question.type === 'text' && (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={responses[`q${idx}`] || ''}
                                                onChange={(e) =>
                                                    handleResponseChange(`q${idx}`, e.target.value)
                                                }
                                                placeholder="Share your feedback..."
                                                variant="outlined"
                                                sx={{
                                                    '& .MuiOutlinedInput-root': {
                                                        bgcolor: 'white',
                                                        '&:hover fieldset': {
                                                            borderColor: '#dc2626',
                                                        },
                                                        '&.Mui-focused fieldset': {
                                                            borderColor: '#653a8a',
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                    </Paper>
                                ))}
                            </>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                            mt: 4,
                            pt: 3,
                            borderTop: '1px solid #e2e8f0',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => navigate(-1)}
                            disabled={submitting}
                            sx={{
                                px: 4,
                                py: 1.2,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                borderColor: '#cbd5e1',
                                color: '#64748b',
                                '&:hover': {
                                    borderColor: '#94a3b8',
                                    bgcolor: '#f8fafc',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={submitting || !user || alreadySubmitted}
                            sx={{
                                px: 4,
                                py: 1.2,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '1rem',
                                fontWeight: 600,
                                background: 'linear-gradient(135deg, #dc2626 0%, #764ba2 100%)',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #dc2626 0%, #653a8a 100%)',
                                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.4)',
                                },
                                '&:disabled': {
                                    background: '#cbd5e1',
                                    color: '#94a3b8',
                                },
                            }}
                        >
                            {submitting ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} sx={{ color: 'white' }} />
                                    Submitting...
                                </Box>
                            ) : (
                                'Submit Form'
                            )}
                        </Button>
                    </Box>
                </Paper>
            </Container>

            <Box
                sx={{
                    py: 3,
                    mt: 6,
                    bgcolor: 'white',
                    borderTop: '1px solid #e2e8f0',
                }}
            >
                <Container maxWidth="lg">
                    <Typography
                        variant="body2"
                        align="center"
                        sx={{ color: '#94a3b8' }}
                    >
                        © 2024 Form Submission Portal. All rights reserved.
                    </Typography>
                </Container>
            </Box>
            <Dialog
                open={successOpen}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        px: 4,
                        py: 3,
                        textAlign: 'center',
                    },
                }}
            >
                <DialogContent>
                    <CheckCircleIcon
                        sx={{
                            fontSize: 80,
                            color: '#22c55e', // green
                            mb: 2,
                        }}
                    />
                    <Typography variant="h6" fontWeight={600}>
                        Form Submitted Successfully
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Redirecting to home...
                    </Typography>
                </DialogContent>
            </Dialog>

        </Box>
    );
}